import { SidebarTagProps } from "components/templates/TagsSidebar";
import { useCreateAssetTagsMutation } from "graphqlBase/MappingAssetTag/__generated__/createAssetTags";
import { useCreateMappingAssetTagsMutation } from "graphqlBase/MappingAssetTag/__generated__/createMappingAssetTags";
import { MappingAssetTagCreateType } from "graphqlBase/types";
import { useRecoilValue, useSetRecoilState } from "recoil";
import { assetTagsforTagsListAtom, assetTagsToCreateAtom } from "../recoilState/assetTagsState";
import { assignedAssetTagErrorAtom } from "../recoilState/tagModalState";

const useCreateAssetTags = ({
  selectedAssetIds,
  afterMutation,
}: {
  afterMutation?: () => void;
  selectedAssetIds: string[] | undefined;
}) => {
  const createAssetTags = useRecoilValue(assetTagsToCreateAtom);
  const assetTagsForTagsList = useRecoilValue(assetTagsforTagsListAtom);
  const setAssignedAssetTagError = useSetRecoilState(assignedAssetTagErrorAtom);

  const [
    createMappingAssetTagsMutation,
    { loading: createMappingAssetTagsLoading },
  ] = useCreateMappingAssetTagsMutation();

  const [createAssetTagsMutation, { loading: createAssetTagsLoading }] = useCreateAssetTagsMutation();

  const handleCreateAssetTags = async () => {
    const uniqueSelectedAssetIds = Array.from(new Set((selectedAssetIds ?? []).filter(Boolean)));
    if (!uniqueSelectedAssetIds.length) return;

    try {
      const { data } = await createAssetTagsMutation({ variables: { createAssetTags } });

      // Only create mappings for tags marked as newly mapped
      const newlyMappedTags = (assetTagsForTagsList ?? []).filter((tag) => tag.isNewMapped);

      const createMappingAssetTags = uniqueSelectedAssetIds.reduce(
        (accAsset: MappingAssetTagCreateType[], currAssetId) => {
          const temp = newlyMappedTags.reduce((accChip: MappingAssetTagCreateType[], currTag) => {
            if (!currTag.id) return accChip;
            return accChip.concat({ assetTagId: currTag.id, assetId: currAssetId });
          }, []);

          const newMappings: MappingAssetTagCreateType[] =
            data?.createAssetTags?.reduce((accNewMappings: MappingAssetTagCreateType[], newTag) => {
              if (!newTag?.id) return accNewMappings;
              return accNewMappings.concat({ assetTagId: newTag.id, assetId: currAssetId });
            }, []) ?? [];

          return accAsset.concat(temp, newMappings);
        },
        []
      );

      if (createMappingAssetTags.length > 0) {
        await createMappingAssetTagsMutation({
          variables: { createMappingAssetTags },
        });
        if (afterMutation) {
          afterMutation();
        }
      }
    } catch (error) {
      setAssignedAssetTagError("This tag is already assigned");
      setTimeout(() => {
        setAssignedAssetTagError(undefined);
      }, 3000);
    }
  };

  return {
    createAssetTagsLoading: createMappingAssetTagsLoading || createAssetTagsLoading,
    handleCreateAssetTags,
  };
};

export default useCreateAssetTags;
